// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	MiddleClickAutoscrollControl,
	SmoothScrollingControl,
	TextSelectionControl,
	VideoSeekThumbnailsControl,
} from '@app/features/user/components/modals/tabs/advanced_settings_tab/AdvancedAccessibilityControls';
import {
	FavoritesControl,
	HideKeyboardHintsControl,
	ShowNekoControl,
	VoiceChannelJoinBehaviorControl,
} from '@app/features/user/components/modals/tabs/advanced_settings_tab/AdvancedAppearanceControls';
import {
	AutoSendGifsControl,
	ExpressionAutocompleteControl,
	HideMutedChannelsByDefaultControl,
	InputButtonsControl,
	MediaButtonsControl,
	MessageActionBarControl,
	ReverseImageSearchControl,
	SaveGifFavoritesControl,
	SearchEnginesControl,
	ScrollToBottomOnSendControl,
	SkipMarkAllAsReadControl,
	StripTrackingControl,
	TranslatorsControl,
	TrustAllDomainsControl,
} from '@app/features/user/components/modals/tabs/advanced_settings_tab/AdvancedChatControls';
import {
	DeveloperModeControl,
	NativeVoiceEngineControl,
} from '@app/features/user/components/modals/tabs/advanced_settings_tab/AdvancedClientDeveloperControls';
import {
	FirstClickPassThroughControl,
	HardwareAccelerationControl,
	NativeTitleBarControl,
	StayInteractiveUnfocusedControl,
} from '@app/features/user/components/modals/tabs/advanced_settings_tab/AdvancedDesktopControls';
import {UnreadBadgeCustomizationControl} from '@app/features/user/components/modals/tabs/advanced_settings_tab/AdvancedExperimentalControls';
import {PushInactiveTimeoutControl} from '@app/features/user/components/modals/tabs/advanced_settings_tab/AdvancedNotificationsControls';
import {NewDeviceAlertsControl} from '@app/features/user/components/modals/tabs/advanced_settings_tab/AdvancedVoiceControls';
import {
	OpenH264Control,
	ScreenShareCodecControl,
	ScreenShareEncoderControls,
	ScreenSharePreviewBehaviorControl,
} from '@app/features/user/components/modals/tabs/advanced_settings_tab/AdvancedVideoControls';
import type {SearchableSettingItem} from '@app/features/user/components/settings_utils/SettingsSectionRegistry';
import {observer} from 'mobx-react-lite';

export const DIRECT_CONTROL_ITEM_IDS = new Set([
	'accessibility-text-selection',
	'accessibility-video-seek-thumbnails',
	'accessibility-smooth-scrolling',
	'accessibility-middle-click-autoscroll',
	'appearance-show-neko',
	'appearance-hide-keyboard-hints',
	'appearance-voice-channel-join-behavior',
	'appearance-enable-favorites',
	'chat-settings-auto-send-gifs',
	'chat-settings-save-gif-favorites',
	'chat-settings-message-action-bar',
	'chat-settings-media-buttons',
	'chat-settings-strip-tracking',
	'chat-settings-trust-domains',
	'chat-settings-search-engines',
	'chat-settings-translators',
	'chat-settings-reverse-image-search',
	'chat-settings-expression-autocomplete',
	'chat-settings-input-buttons',
	'chat-settings-scroll-to-bottom-on-send',
	'chat-settings-skip-mark-all-as-read-confirmation',
	'chat-settings-hide-muted-channels',
	'voice-video-new-device-alerts',
	'voice-video-screen-share-codec',
	'voice-video-openh264-codec',
	'voice-video-screen-share-preview-behavior',
	'voice-video-screen-share-encoder-controls',
	'notifications-afk-timeout',
	'advanced-unread-badge-customization',
	'client-developer-mode',
	'client-native-voice-engine',
	'advanced-stay-interactive-unfocused',
	'first-click-pass-through',
	'advanced-native-title-bar',
	'advanced-hardware-acceleration',
]);

export const FULL_WIDTH_CONTROL_ITEM_IDS = new Set([
	'appearance-voice-channel-join-behavior',
	'voice-video-screen-share-codec',
	'voice-video-screen-share-preview-behavior',
	'notifications-afk-timeout',
]);

export const AdvancedSettingControl = observer(({item}: {item: SearchableSettingItem}) => {
	switch (item.id) {
		case 'accessibility-text-selection':
			return <TextSelectionControl />;
		case 'accessibility-video-seek-thumbnails':
			return <VideoSeekThumbnailsControl />;
		case 'accessibility-smooth-scrolling':
			return <SmoothScrollingControl />;
		case 'accessibility-middle-click-autoscroll':
			return <MiddleClickAutoscrollControl />;
		case 'appearance-show-neko':
			return <ShowNekoControl />;
		case 'appearance-hide-keyboard-hints':
			return <HideKeyboardHintsControl />;
		case 'appearance-voice-channel-join-behavior':
			return <VoiceChannelJoinBehaviorControl />;
		case 'appearance-enable-favorites':
			return <FavoritesControl />;
		case 'chat-settings-auto-send-gifs':
			return <AutoSendGifsControl />;
		case 'chat-settings-save-gif-favorites':
			return <SaveGifFavoritesControl />;
		case 'chat-settings-message-action-bar':
			return <MessageActionBarControl title={item.label} />;
		case 'chat-settings-media-buttons':
			return <MediaButtonsControl title={item.label} />;
		case 'chat-settings-strip-tracking':
			return <StripTrackingControl />;
		case 'chat-settings-trust-domains':
			return <TrustAllDomainsControl />;
		case 'chat-settings-search-engines':
			return <SearchEnginesControl title={item.label} />;
		case 'chat-settings-translators':
			return <TranslatorsControl title={item.label} />;
		case 'chat-settings-reverse-image-search':
			return <ReverseImageSearchControl title={item.label} />;
		case 'chat-settings-expression-autocomplete':
			return <ExpressionAutocompleteControl title={item.label} />;
		case 'chat-settings-input-buttons':
			return <InputButtonsControl title={item.label} />;
		case 'chat-settings-scroll-to-bottom-on-send':
			return <ScrollToBottomOnSendControl />;
		case 'chat-settings-skip-mark-all-as-read-confirmation':
			return <SkipMarkAllAsReadControl />;
		case 'chat-settings-hide-muted-channels':
			return <HideMutedChannelsByDefaultControl />;
		case 'voice-video-new-device-alerts':
			return <NewDeviceAlertsControl />;
		case 'voice-video-screen-share-codec':
			return <ScreenShareCodecControl />;
		case 'voice-video-openh264-codec':
			return <OpenH264Control />;
		case 'voice-video-screen-share-preview-behavior':
			return <ScreenSharePreviewBehaviorControl />;
		case 'voice-video-screen-share-encoder-controls':
			return <ScreenShareEncoderControls title={item.label} />;
		case 'notifications-afk-timeout':
			return <PushInactiveTimeoutControl />;
		case 'advanced-unread-badge-customization':
			return <UnreadBadgeCustomizationControl />;
		case 'client-developer-mode':
			return <DeveloperModeControl />;
		case 'client-native-voice-engine':
			return <NativeVoiceEngineControl />;
		case 'advanced-stay-interactive-unfocused':
			return <StayInteractiveUnfocusedControl />;
		case 'first-click-pass-through':
			return <FirstClickPassThroughControl />;
		case 'advanced-native-title-bar':
			return <NativeTitleBarControl />;
		case 'advanced-hardware-acceleration':
			return <HardwareAccelerationControl />;
		default:
			return null;
	}
});
